<template>
    <main class="p-2 w-full h-full max-h-main flex flex-col items-start min-h-main">
        <div class="w-full">
            <div class="flex w-full gap-2 items-center">
                <span class="flex-grow"></span>
                <a
                    :href="lastInvoicesExport ? lastInvoicesExport.url : '#'"
                    ref="lastPdfLink"
                    download
                    target="_blank"
                    class="underline text-xs"
                    v-if="lastInvoicesExport && lastInvoicesExport.generationSucceeded"
                    :title="$t('commons.lastExport') + ' (' + humanizeDate(lastInvoicesExport.createdAt) + ')'"
                >
                    {{ $t('commons.lastExport') }}
                </a>
                <app-button
                    size="mini"
                    class="p-2 shadow"
                    v-click-helper="openPdf"
                    :aria-label="$t('commons.toPdf')"
                    :title="$t('commons.toPdf')"
                    :disabled="lastInvoicesExport && !lastInvoicesExport.generationEndDate"
                    :icon="
                        lastInvoicesExport && !lastInvoicesExport.generationEndDate
                            ? 'icon-rotate-right'
                            : 'icon-file-pdf-outline'
                    "
                    :icon-class="
                        lastInvoicesExport && !lastInvoicesExport.generationEndDate ? 'animate animate-spin' : ''
                    "
                />
            </div>
            <app-multi-picker
                ref="filter"
                icon="icon-magnify"
                :allowStringCriteria="true"
                class="w-full my-2"
                v-model="filterValue"
                :options="filterOptions"
                :strictMatching="true"
            >
                <template v-slot:option="{ option }">
                    <span>{{ option.fullName || option.name }}</span>
                    <span class="text-xs text-gray-600 ml-1">{{ option.criteriaType }}</span>
                </template>
            </app-multi-picker>
        </div>
        <div class="flex w-full flex-grow overflow-auto flex-col text-xs relative">
            <div v-if="loading" class="flex justify-center">
                <icon-rotate-right class="animate animate-spin"></icon-rotate-right>
            </div>
            <table class="table-fixed" v-else>
                <thead class="sticky top-0 bg-white">
                    <tr>
                        <th colspan="2"></th>
                        <th colspan="3" class="border bg-yellow-500">{{ $t('amendments.title') }}</th>
                        <th class="border bg-yellow-600">{{ $t('cie.title') }}</th>
                        <th colspan="4" class="border bg-yellow-500">{{ $t('deductions.title') }}</th>
                        <th class="border bg-yellow-600">{{ $t('statements.total') }}</th>
                        <th colspan="2" class="border bg-green-200">{{ $t('amendments.payementDelegation') }}</th>
                        <th colspan="7" class="border bg-red-300">{{ $t('invoices.title') }}</th>
                        <th class="border bg-blue-400">{{ $t('statements.controls') }}</th>
                    </tr>
                    <tr>
                        <th class="text-left border-r p-1" style="width: 15rem">
                            {{ $t('commons.bundle') }}
                        </th>
                        <th class="text-left border-r p-1">
                            {{ $t('invoices.mainCompany') }}
                        </th>
                        <th class="text-center border-r p-1">
                            <span :title="$t('statements.marketTips')">{{ $t('statements.market') }}</span>
                        </th>
                        <th class="text-center border-r p-1">
                            <div :title="$t('statements.allAmendmentsTips')">
                                {{ $t('statements.allAmendments') }}
                            </div>
                        </th>
                        <th class="text-center border-r p-1">
                            {{ $t('statements.marketPlusAmendments') }}
                        </th>
                        <th class="text-center border-r p-1">
                            <span :title="$t('statements.cieTips')">{{ $t('statements.cie') }}</span>
                        </th>
                        <th class="text-center border-r p-1">
                            {{ $t('statements.caution') }}
                        </th>
                        <th class="text-center border-r p-1">
                            {{ $t('deductions.types.garanteeDeduction') }}
                        </th>
                        <th class="text-center border-r p-1">
                            {{ $t('statements.deductions') }}
                        </th>
                        <th class="text-center border-r p-1">
                            {{ $t('statements.penalties') }}
                        </th>
                        <th class="text-center border-r p-1">
                            <span :title="$t('statements.grantTotalTips')">{{ $t('statements.total') }}</span>
                        </th>
                        <th class="text-center border-r p-1">{{ $t('amendments.totalSubcontractors') }}</th>
                        <th class="text-center border-r p-1">{{ $t('amendments.totalMainCompany') }}</th>
                        <th class="text-center border-r p-1">
                            {{ $t('statements.situationMainCompany') }}
                        </th>
                        <th class="text-center border-r p-1">
                            {{ $t('statements.situationSubcontractors') }}
                        </th>
                        <th class="text-center border-r p-1">{{ $t('statements.situationTotal') }}</th>
                        <th class="text-center border-r p-1">{{ $t('statements.situationGaranteeDeduction') }}</th>
                        <th class="text-center border-r p-1">{{ $t('statements.situationCIE') }}</th>
                        <th class="text-center border-r p-1">{{ $t('statements.situationPenalties') }}</th>
                        <th class="text-center border-r p-1">{{ $t('statements.situationOtherDeductions') }}</th>
                        <th class="text-center border-r p-1">{{ $t('statements.controlBalance') }}</th>
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        <td class="text-right border-r p-1" colspan="2">
                            <i>{{ $t('statements.allBundles') }}</i>
                        </td>
                        <td class="text-right border-r p-1">{{ $n(totalMarket, currencyFormat) }}</td>
                        <td class="text-right border-r p-1">{{ $n(totalAmendments, currencyFormat) }}</td>
                        <td class="text-right border-r p-1">{{ $n(totalMarketPlusAmendments, currencyFormat) }}</td>
                        <td class="text-right border-r p-1">
                            <span
                                :title="
                                    $n(totalCie, currencyFormat) + '\n - ' + $n(totalCieResponsability, currencyFormat)
                                "
                            >
                                {{ $n(totalCie - totalCieResponsability, currencyFormat) }}
                            </span>
                        </td>
                        <td class="text-right border-r p-1">{{ $n(totalCaution, currencyFormat) }}</td>
                        <td class="text-right border-r p-1">{{ $n(totalGaranteeDeductions, currencyFormat) }}</td>
                        <td class="text-right border-r p-1">{{ $n(totalDeductions, currencyFormat) }}</td>
                        <td class="text-right border-r p-1">{{ $n(totalPenalties, currencyFormat) }}</td>
                        <td class="text-right border-r p-1">{{ $n(grantTotal, currencyFormat) }}</td>
                        <td class="text-right border-r p-1">{{ $n(totalSubcontractors, currencyFormat) }}</td>
                        <td class="text-right border-r p-1">{{ $n(totalMainCompany, currencyFormat) }}</td>

                        <td class="text-right border-r p-1">
                            {{ $n(totalSituationTotalMainCompany, currencyFormat) }}
                        </td>

                        <td class="text-right border-r p-1">
                            {{ $n(totalSituationTotalSubcontractors, currencyFormat) }}
                        </td>
                        <td class="text-right border-r p-1">{{ $n(totalSituationTotal, currencyFormat) }}</td>

                        <td class="text-right border-r p-1">
                            {{ $n(totalSituationGaranteeDeduction, currencyFormat) }}
                        </td>

                        <td class="text-right border-r p-1">{{ $n(totalSituationCIE, currencyFormat) }}</td>

                        <td class="text-right border-r p-1">{{ $n(totalSituationPenalties, currencyFormat) }}</td>
                        <td class="text-right border-r p-1">
                            {{ $n(totalSituationOtherDeductions, currencyFormat) }}
                        </td>
                    </tr>

                    <tr v-if="filteredItems.length > 1 && filterValue.length > 0">
                        <td class="text-right border-r p-1" colspan="2">
                            <i>{{ $t('statements.allFilteredLines') }}</i>
                        </td>
                        <td class="text-right border-r p-1">{{ $n(filteredTotalMarket, currencyFormat) }}</td>
                        <td class="text-right border-r p-1">{{ $n(filteredTotalAmendments, currencyFormat) }}</td>
                        <td class="text-right border-r p-1">
                            {{ $n(filteredTotalMarketPlusAmendments, currencyFormat) }}
                        </td>
                        <td class="text-right border-r p-1">
                            <span
                                :title="
                                    $n(filteredTotalCie, currencyFormat) +
                                    '\n - ' +
                                    $n(filteredTotalCieResponsability, currencyFormat)
                                "
                            >
                                {{ $n(filteredTotalCie - filteredTotalCieResponsability, currencyFormat) }}
                            </span>
                        </td>
                        <td class="text-right border-r p-1">{{ $n(filteredTotalCaution, currencyFormat) }}</td>
                        <td class="text-right border-r p-1">
                            {{ $n(filteredTotalGaranteeDeductions, currencyFormat) }}
                        </td>
                        <td class="text-right border-r p-1">{{ $n(filteredTotalDeductions, currencyFormat) }}</td>
                        <td class="text-right border-r p-1">{{ $n(filteredTotalPenalties, currencyFormat) }}</td>
                        <td class="text-right border-r p-1">{{ $n(filteredGrantTotal, currencyFormat) }}</td>
                        <td class="text-right border-r p-1">
                            {{ $n(filteredSituationTotalMainCompany, currencyFormat) }}
                        </td>

                        <td class="text-right border-r p-1">
                            {{ $n(filteredSituationTotalSubcontractors, currencyFormat) }}
                        </td>
                        <td class="text-right border-r p-1">{{ $n(filteredSituationTotal, currencyFormat) }}</td>

                        <td class="text-right border-r p-1">
                            {{ $n(filteredSituationGaranteeDeduction, currencyFormat) }}
                        </td>

                        <td class="text-right border-r p-1">{{ $n(filteredSituationCIE, currencyFormat) }}</td>

                        <td class="text-right border-r p-1">{{ $n(filteredSituationPenalties, currencyFormat) }}</td>
                        <td class="text-right border-r p-1">
                            {{ $n(filteredSituationOtherDeductions, currencyFormat) }}
                        </td>
                    </tr>
                    <template v-for="bundle in filteredItems">
                        <tr class="odd:bg-blue-50 border-t">
                            <td class="text-left border-r p-1">
                                {{ bundle.label }}
                            </td>
                            <td class="text-left border-r p-1">
                                {{ bundle.company?.name }}
                            </td>
                            <td class="text-right border-r p-1">{{ $n(bundle.totalMarket, currencyFormat) }}</td>
                            <td class="text-right border-r p-1">{{ $n(bundle.totalAmendments, currencyFormat) }}</td>
                            <td class="text-right border-r p-1">
                                {{ $n(bundle.totalMarketPlusAmendments, currencyFormat) }}
                            </td>
                            <td class="text-right border-r p-1">
                                <span
                                    :title="
                                        $n(bundle.totalCie, currencyFormat) +
                                        '\n - ' +
                                        $n(bundle.totalCieResponsability, currencyFormat)
                                    "
                                >
                                    {{ $n(bundle.totalCie - bundle.totalCieResponsability, currencyFormat) }}
                                </span>
                            </td>

                            <td class="text-right border-r p-1">{{ $n(bundle.totalCaution, currencyFormat) }}</td>
                            <td class="text-right border-r p-1">
                                {{ $n(bundle.totalGaranteeDeductions, currencyFormat) }}
                            </td>
                            <td class="text-right border-r p-1">{{ $n(bundle.totalDeductions, currencyFormat) }}</td>
                            <td class="text-right border-r p-1">{{ $n(bundle.totalPenalties, currencyFormat) }}</td>
                            <td class="text-right border-r p-1">{{ $n(bundle.grantTotal, currencyFormat) }}</td>

                            <td class="text-right border-r p-1">
                                {{ $n(bundle.totalSubcontractors, currencyFormat) }}
                            </td>
                            <td class="text-right border-r p-1">{{ $n(bundle.totalMainCompany, currencyFormat) }}</td>

                            <td class="text-right border-r p-1">
                                {{ $n(bundle.situationTotalMainCompany, currencyFormat) }}
                            </td>
                            <td class="text-right border-r p-1">
                                {{ $n(bundle.situationTotalSubcontractors, currencyFormat) }}
                            </td>
                            <td class="text-right border-r p-1">
                                {{ $n(bundle.situationTotal, currencyFormat) }}
                            </td>
                            <td class="text-right border-r p-1">
                                {{ $n(bundle.situationGaranteeDeduction, currencyFormat) }}
                            </td>
                            <td class="text-right border-r p-1">{{ $n(bundle.situationCIE, currencyFormat) }}</td>
                            <td class="text-right border-r p-1">
                                {{ $n(bundle.situationPenalties, currencyFormat) }}
                            </td>
                            <td class="text-right border-r p-1">
                                {{ $n(bundle.situationOtherDeductions, currencyFormat) }}
                            </td>
                            <td class="text-right border-r p-1">
                                {{ $n(bundle.controlBalance, currencyFormat) }}
                            </td>
                        </tr>
                    </template>
                </tbody>
            </table>
        </div>
        <app-quick-actions :options="quickActions" @choose="$event.run()"></app-quick-actions>
    </main>
</template>

<script>
import AppCheckbox from '../../components/app-checkbox/AppCheckbox';
import { isMobile } from '@/state/state';
import { filterMatch, get, getMapById, reduceSumOn, sortBy, uniqBy } from '@/services/sanitize.service';
import AppList from '../../components/appList/AppList';
import AppSelect from '@/components/appSelect/AppSelect';
import AppMultiPicker from '@/components/appMultiPicker/AppMultiPicker';
import AppQuickActions from '@/components/appQuickActions/AppQuickActions';
import AppButton from '@/components/appButton/AppButton';
import { getInvoices } from '@/features/invoices/invoices.service';
import AppPreparationVisaStatus from '@/components/appPreparationVisaStatus/AppPreparationVisaStatus';
import AppDateLink from '@/components/appDateLink/AppDateLink';
import AppPopup from '@/components/app-popup/AppPopup';
import AppBundlePicker from '@/components/appBundlePicker';
import AppDateInput from '@/components/appDateInput/AppDateInput';
import AppFooter from '@/components/appFooter/AppFooter';
import AppInputText from '@/components/appInputText/AppInputText';
import AppCancel from '@/components/appCancel/AppCancel';
import IconMenuDown from '@/icons/IconMenuDown';
import IconMenuUp from '@/icons/IconMenuUp';
import AppNumberInput from '@/components/appNumberInput/AppNumberInput';
import AppNumberLink from '@/components/appNumberLink/AppNumberLink';
import { queryProject } from '@/features/projects/projects.service';

import { combineLatest } from 'rxjs';
import { getBundles } from '@/features/bundles/bundles.service';
import { getCompanies } from '@/features/companies/companies.service';
import AppTips from '@/components/app-tips/AppTips.vue';
import AppTextarea from '@/components/app-textarea/AppTextarea.vue';
import AppLabel from '@/components/appLabel/AppLabel.vue';
import IconAlertCircle from '@/icons/IconAlertCircle.vue';
import IconAlertOutline from '@/icons/IconAlertOutline.vue';
import { replicateProjects } from '@/rxdb/database';
import { error, info } from '@/features/toasts/toats.service';
import { createInvoicesExport, getLastInvoicesExport } from '@/features/invoices/invoicesExports.service';
import { humanizeDate } from '@/filters/dateFilter';
import vueClickHelper from 'vue-click-helper';
import { getAmendments } from '@/features/amendments/amendments.service';
import {
    getSituationCIE,
    getSituationGaranteeDeduction,
    getSituationMainCompany,
    getSituationOtherDeductions,
    getSituationPenalties,
    getSituationSubcontractors,
    getTotalAmendments,
    getTotalCaution,
    getTotalCie,
    getTotalCieResponsability,
    getTotalDeductions,
    getTotalGaranteeDeductions,
    getTotalMarket,
    getTotalPenalties,
} from '@/features/statements/statements.service';
import { getDeductions } from '@/features/deductions/deductions.service';
import { getCIEs } from '@/features/cie/CIE.service';

export default {
    components: {
        IconAlertOutline,
        IconAlertCircle,
        AppLabel,
        AppTextarea,
        AppTips,
        AppNumberLink,
        AppNumberInput,
        IconMenuUp,
        IconMenuDown,
        AppCancel,
        AppInputText,
        AppFooter,
        AppDateInput,
        AppBundlePicker,
        AppPopup,
        AppDateLink,
        AppPreparationVisaStatus,
        AppButton,
        AppQuickActions,
        AppMultiPicker,
        AppSelect,
        AppList,
        AppCheckbox,
    },
    directives: { 'click-helper': vueClickHelper },
    async created() {
        this.restoreFilter();
        queryProject(this.$route.params.projectId).then((project) => {
            this.currencyFormat.currency = project.currency;
        });
        this.init();
    },
    computed: {
        filteredTotalMarket() {
            return reduceSumOn(this.filteredItems, 'totalMarket');
        },
        filteredTotalAmendments() {
            return reduceSumOn(this.filteredItems, 'totalAmendments');
        },
        filteredTotalMarketPlusAmendments() {
            return reduceSumOn(this.filteredItems, 'totalMarketPlusAmendments');
        },
        filteredTotalCaution() {
            return reduceSumOn(this.filteredItems, 'totalCaution');
        },
        filteredTotalDeductions() {
            return reduceSumOn(this.filteredItems, 'totalDeductions');
        },
        filteredTotalGaranteeDeductions() {
            return reduceSumOn(this.filteredItems, 'totalGaranteeDeductions');
        },
        filteredTotalPenalties() {
            return reduceSumOn(this.filteredItems, 'totalPenalties');
        },
        filteredTotalCie() {
            return reduceSumOn(this.filteredItems, 'totalCie');
        },
        filteredTotalCieResponsability() {
            return reduceSumOn(this.filteredItems, 'totalCieResponsability');
        },
        filteredGrantTotal() {
            return reduceSumOn(this.filteredItems, 'grantTotal');
        },
        filteredSituationTotalMainCompany() {
            return reduceSumOn(this.filteredItems, 'situationTotalMainCompany');
        },
        filteredSituationTotalSubcontractors() {
            return reduceSumOn(this.filteredItems, 'situationTotalSubcontractors');
        },
        filteredSituationTotal() {
            return reduceSumOn(this.filteredItems, 'situationTotal');
        },
        filteredSituationGaranteeDeduction() {
            return reduceSumOn(this.filteredItems, 'situationGaranteeDeduction');
        },
        filteredSituationCIE() {
            return reduceSumOn(this.filteredItems, 'situationCIE');
        },
        filteredSituationPenalties() {
            return reduceSumOn(this.filteredItems, 'situationPenalties');
        },
        filteredSituationOtherDeductions() {
            return reduceSumOn(this.filteredItems, 'situationOtherDeductions');
        },
        filteredItems() {
            let result = this.filterFn(this.filterValue).map((item) => ({
                ...item,
                isSelected: this.selection.includes(item.id),
            }));
            if (this.sortKey) {
                result = sortBy(result, (item) => {
                    const text = item.code ? item.code + ' ' + item.object : item.object;
                    if (this.sortKey === 'object') {
                        return text;
                    } else if (this.sortKey === 'emissionDate') {
                        return item.emissionDate + ' ' + text;
                    } else if (this.sortKey === 'emitter') {
                        return (item.bundle ? item.bundle.label : null) + text;
                    }
                });
                if (!this.sortAsc) {
                    result.reverse();
                }
            }
            return result;
        },
        filterOptions() {
            return [
                {
                    isGroup: true,
                    name: this.$t('commons.emitterCriteriaType'),
                    id: this.$t('commons.emitterCriteriaType'),
                    children: this.bundles.map((bundle, index) => ({
                        ...bundle,
                        name: bundle.label,
                        firstOfCriteriaType: index === 0,
                        criteriaType: this.$t('commons.emitterCriteriaType'),
                        _isBundleCriteria: true,
                    })),
                },
            ];
        },
    },
    methods: {
        humanizeDate,
        init() {
            this.subscriptions = [
                combineLatest([
                    getBundles(this.$route.params.projectId),
                    getInvoices(this.$route.params.projectId),
                    getCompanies(this.$route.params.projectId),
                    getAmendments(this.$route.params.projectId),
                    getDeductions(this.$route.params.projectId),
                    getCIEs(this.$route.params.projectId),
                ]).subscribe(async ([bundles, invoices, companies, amendments, deductions, cies]) => {
                    this.amendments = uniqBy(amendments, 'groupId').filter(
                        (amendment) => amendment.quoteStatus === 'validated',
                    );
                    const relevantInvoices = uniqBy(invoices, 'groupId').filter(
                        (invoice) => invoice.conclusion === 'validated',
                    );
                    const relevantDeductions = deductions.filter(
                        (deduction) => deduction.deductionStatus === 'definitive',
                    );
                    const relevantCIEs = cies.filter((cie) => cie.quoteStatus === 'validated');
                    this.totalMarket = getTotalMarket(this.amendments);
                    this.totalAmendments = getTotalAmendments(this.amendments);
                    this.totalMarketPlusAmendments = this.totalMarket + this.totalAmendments;
                    this.totalCaution = getTotalCaution(relevantDeductions);
                    this.totalGaranteeDeductions = getTotalGaranteeDeductions(relevantDeductions);
                    this.totalDeductions = getTotalDeductions(relevantDeductions);
                    this.totalPenalties = getTotalPenalties(relevantDeductions);
                    this.totalCie = getTotalCie(relevantCIEs);
                    this.totalCieResponsability = getTotalCieResponsability(relevantCIEs, null);
                    this.grantTotal =
                        this.totalMarket +
                        this.totalAmendments -
                        this.totalDeductions -
                        this.totalPenalties +
                        this.totalCie -
                        this.totalCieResponsability;
                    this.totalSubcontractors = reduceSumOn(
                        [...relevantCIEs, ...this.amendments].filter((amendment) => amendment.hasPaymentDelegation),
                        'amountTTC',
                    );
                    this.totalMainCompany = this.grantTotal - this.totalSubcontractors;
                    this.companies = companies.map((company) => ({
                        ...company,
                        bundleIds: bundles
                            .filter((bundle) => bundle.companyId === company.id)
                            .map((bundle) => bundle.id),
                    }));
                    const companyMap = getMapById(companies);
                    this.bundles = sortBy(
                        bundles.map((bundle) => ({
                            ...bundle,
                            company: companyMap[bundle.companyId],
                            filterString: bundle.label + companyMap[bundle.companyId]?.name,
                        })),
                        (bundle) => bundle.label,
                    );
                    this.bundleMap = getMapById(this.bundles);
                    this.items = this.bundles.map((bundle) => {
                        const emitterId = bundle.id;
                        const bundleInvoices = relevantInvoices.filter((invoice) => invoice.emitterId === emitterId);
                        const amendments = this.amendments.filter((amendment) => amendment.bundleId === emitterId);
                        const bundleCies = relevantCIEs.filter((cie) => cie.bundleId === emitterId);
                        const totalCIEResponsability = getTotalCieResponsability(relevantCIEs, emitterId);
                        const situationTotalMainCompany = getSituationMainCompany(bundleInvoices, emitterId);
                        const situationTotalSubcontractors = getSituationSubcontractors(bundleInvoices, emitterId);
                        const situationCIE = getSituationCIE(bundleInvoices);
                        const situationGaranteeDeduction = getSituationGaranteeDeduction(bundleInvoices);
                        const situationPenalties = getSituationPenalties(bundleInvoices);
                        const situationOtherDeductions = getSituationOtherDeductions(bundleInvoices);
                        const bundleDeductions = deductions.filter((deduction) => deduction.bundleId === emitterId);
                        const totalMarket = getTotalMarket(amendments);
                        const totalAmendments = getTotalAmendments(amendments);
                        const totalMarketPlusAmendments = totalMarket + totalAmendments;
                        const totalCaution = getTotalCaution(bundleDeductions);
                        const totalDeductions = getTotalDeductions(bundleDeductions);
                        const totalGaranteeDeductions = getTotalGaranteeDeductions(bundleDeductions);
                        const totalPenalties = getTotalPenalties(bundleDeductions);
                        const totalCie = getTotalCie(bundleCies);
                        const totalCieResponsability = totalCIEResponsability;
                        const grantTotal =
                            totalMarket +
                            totalAmendments +
                            totalCie -
                            totalDeductions -
                            totalPenalties -
                            totalGaranteeDeductions -
                            totalCieResponsability;
                        const situationTotal = situationTotalSubcontractors + situationTotalMainCompany;
                        const totalSubcontractors = reduceSumOn(
                            [...bundleCies, ...amendments].filter((amendment) => amendment.hasPaymentDelegation),
                            'amountTTC',
                        );
                        const totalMainCompany = grantTotal - totalSubcontractors;
                        return {
                            ...bundle,
                            relatedInvoices: bundleInvoices,
                            totalMarket,
                            totalAmendments,
                            totalMarketPlusAmendments,
                            totalCaution,
                            totalGaranteeDeductions,
                            totalDeductions,
                            totalPenalties,
                            totalCie,
                            totalCieResponsability,
                            grantTotal,
                            totalMainCompany,
                            totalSubcontractors,
                            situationTotalMainCompany,
                            situationTotalSubcontractors,
                            situationTotal,
                            situationGaranteeDeduction,
                            situationCIE,
                            situationPenalties,
                            situationOtherDeductions,
                            controlBalance: grantTotal - situationTotal,
                        };
                    });

                    this.totalSituationTotalMainCompany = reduceSumOn(this.items, 'situationTotalMainCompany');
                    this.totalSituationTotalSubcontractors = reduceSumOn(this.items, 'situationTotalSubcontractors');
                    this.totalSituationTotal = reduceSumOn(this.items, 'situationTotal');
                    this.totalSituationGaranteeDeduction = reduceSumOn(this.items, 'situationGaranteeDeduction');
                    this.totalSituationCIE = reduceSumOn(this.items, 'situationCIE');
                    this.totalSituationPenalties = reduceSumOn(this.items, 'situationPenalties');
                    this.totalSituationOtherDeductions = reduceSumOn(this.items, 'situationOtherDeductions');

                    this.scrollToLastVisited();
                    this.lastInvoicesExport = await getLastInvoicesExport(this.$route.params.projectId);
                    this.loading = false;
                }),
            ];
        },
        scrollToLastVisited() {
            const lastVisitedInvoiceId = localStorage.getItem(
                'invoice.lastVisitedInvoiceId.' + this.$route.params.projectId,
            );
            if (lastVisitedInvoiceId) {
                setTimeout(() => {
                    const element = this.$el.querySelector
                        ? this.$el.querySelector('#uuid_' + lastVisitedInvoiceId)
                        : null;
                    if (element) element.scrollIntoView();
                }, 800);
            }
        },
        get,
        sortBy(key) {
            if (key === this.sortKey) {
                this.sortAsc = !this.sortAsc;
            } else {
                this.sortKey = key;
                this.sortAsc = true;
            }
            this.saveFilter(this.filterValue);
        },
        matchBundleFilter(item, bundleIds) {
            return item.id && bundleIds.includes(item.id);
        },
        matchString(stringCriteria, item) {
            if (!stringCriteria || stringCriteria.length === 0) {
                return true;
            }
            return stringCriteria.find((criteria) => filterMatch(item.filterString || item.name, criteria, true));
        },
        saveFilter(filterValue) {
            localStorage.setItem(
                'statements_filter_' + this.$route.params.projectId,
                JSON.stringify({
                    filterValue,
                    sortKey: this.sortKey,
                }),
            );
        },
        restoreFilter() {
            const cache = localStorage.getItem('statements_filter_' + this.$route.params.projectId);
            if (cache) {
                const parsedCache = JSON.parse(cache);
                this.filterValue = parsedCache.filterValue || [];
                this.sortKey = parsedCache.sortKey || 'object';
            }
        },
        filterFn(filter) {
            this.saveFilter(filter);
            const bundleIds = [...filter.filter((aCriteria) => aCriteria._isBundleCriteria).map((bundle) => bundle.id)];
            const stringCriteria = filter
                .filter((aCriteria) => aCriteria._isStringCriteria)
                .map((aCriteria) => aCriteria.content);
            return this.items.filter((item) => {
                const fullCriteria = {
                    matchBundleFilter: bundleIds.length === 0 || this.matchBundleFilter(item, bundleIds),
                    matchString: this.matchString(stringCriteria, item),
                };
                const filterResult = Object.values(fullCriteria).every((value) => !!value);
                if (!filterResult) {
                    this.selection = this.selection.filter((id) => id !== item.id);
                }
                return filterResult;
            });
        },
        openPdf(e, isDoubleClick) {
            if (isDoubleClick) {
                this.$refs.lastPdfLink.click();
            } else {
                this.exportPDF();
            }
        },
        async exportPDF() {
            await replicateProjects(this.$route.params.projectId);
            this.lastInvoicesExport = { generationEndDate: null };
            const lastInvoicesExport = await createInvoicesExport(this.$route.params.projectId, {
                sortKey: this.sortKey,
                sortAsc: this.sortAsc,
                filter: this.filterValue,
            });
            if (lastInvoicesExport) {
                if (lastInvoicesExport.generationSucceeded === true) {
                    info(
                        this.$t('commons.generationSucceed') +
                            `<br/><a href="${lastInvoicesExport.url}"><u>Télécharger le pdf</u></a>`,
                        5000,
                    );
                } else if (lastInvoicesExport.generationSucceeded === false) {
                    error(this.$t('commons.generationFailed'));
                }
            }
            this.lastInvoicesExport = lastInvoicesExport;
        },
    },
    data() {
        return {
            lastInvoicesExport: null,
            currencyFormat: {
                currency: 'EUR',
                currencyDisplay: 'symbol',
                style: 'currency',
                minimumFractionDigits: 2,
            },
            bundleMap: {},
            loading: 0,
            quickActions: [],
            sortKey: 'object',
            sortAsc: true,
            selection: [],
            subscriptions: [],
            bundles: [],
            companies: [],
            isMobile,
            items: [],
            filterValue: [],
            amendments: [],
            totalMarket: 0,
            totalAmendments: 0,
            totalMarketPlusAmendments: 0,
            totalCaution: 0,
            totalGaranteeDeductions: 0,
            totalDeductions: 0,
            totalPenalties: 0,
            totalCie: 0,
            totalCieResponsability: 0,
            grantTotal: 0,
            totalSubcontractors: 0,
            totalMainCompany: 0,

            totalSituationTotalMainCompany: 0,
            totalSituationTotalSubcontractors: 0,
            totalSituationTotal: 0,
            totalSituationGaranteeDeduction: 0,
            totalSituationCIE: 0,
            totalSituationPenalties: 0,
            totalSituationOtherDeductions: 0,
        };
    },
};
</script>
